import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CompactLotteryCard from '@/components/CompactLotteryCard';
import { SEOHead } from '@/components/SEOHead';
import AdBanner from '@/components/AdBanner';
import { useLanguage } from '@/contexts/LanguageContext';
import { TrendingUp, Sparkles, ChevronRight, Award, ChevronDown, ChevronUp, Info } from 'lucide-react';
import LatestIntelligenceFeed from '@/components/LatestIntelligenceFeed';
import { AllLotteriesLinks, FooterLotteryLinks } from '@/components/InternalLinksSEO';
import { API } from '@/config/api';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Final lottery catalog configuration
const LOTTERY_CATALOG = {
  EUROPE: [
    { code: 'swisslotto', name: 'Swiss Lotto 🇨🇭', country: 'Switzerland' },
    { code: 'euromillions', name: 'EuroMillions 🇪🇺', country: 'Europe' },
    { code: 'eurodreams', name: 'EuroDreams 🇪🇺', country: 'Europe' },
    { code: 'superenalotto', name: 'SuperEnalotto 🇮🇹', country: 'Italy' },
    { code: 'laprimitiva', name: 'La Primitiva 🇪🇸', country: 'Spain' },
    { code: 'german-lotto', name: 'German Lotto 6aus49 🇩🇪', country: 'Germany' },
    { code: 'irish-lotto', name: 'Irish Lotto 🇮🇪', country: 'Ireland' },
    { code: 'uk-lotto', name: 'UK Lotto 🇬🇧', country: 'United Kingdom' },
    { code: 'eurojackpot', name: 'EuroJackpot 🇪🇺', country: 'Europe', pending: false },
    { code: 'bonoloto', name: 'BonoLoto 🇪🇸', country: 'Spain', pending: true },
    { code: 'viking-lotto', name: 'Viking Lotto 🇳🇴', country: 'Europe', pending: true },
  ],
  AMERICAS: [
    { code: 'megamillions', name: 'Mega Millions 🇺🇸', country: 'USA', pending: false },
    { code: 'powerball', name: 'Powerball 🇺🇸', country: 'USA', pending: false },
    { code: 'superlottoplus', name: 'SuperLotto Plus 🇺🇸', country: 'USA', pending: true },
    { code: 'lotto-max', name: 'Lotto Max 🇨🇦', country: 'Canada', pending: true },
    { code: 'megasena', name: 'Mega-Sena 🇧🇷', country: 'Brazil', pending: false },
  ],
  AUSTRALIA: [
    { code: 'australian-lotto', name: 'Australian Lotto 🇦🇺', country: 'Australia', pending: false },
    { code: 'powerball-au', name: 'Powerball AU 🇦🇺', country: 'Australia', pending: true },
    { code: 'oz-lotto', name: 'Oz Lotto 🇦🇺', country: 'Australia', pending: true },
  ],
};

// KPI Tooltip Component
const KPITooltip = ({ children }) => (
  <TooltipProvider>
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent 
        className="max-w-xs bg-gray-900 border border-gray-700 p-3 text-xs"
        side="bottom"
      >
        <p className="font-semibold text-white mb-2">What does this mean?</p>
        <p className="text-gray-300 leading-relaxed">
          This index measures how often meaningful prediction events occur
          relative to baseline expectations.
        </p>
        <p className="text-gray-400 mt-2">
          1.05× means events happen about 5% more often than expected.
        </p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

// KPI Interpretation Table Data
const KPI_INTERPRETATION = [
  { range: '0.95× – 0.99×', interpretation: 'Slightly below baseline expectation' },
  { range: '1.00×', interpretation: 'At baseline level' },
  { range: '1.01× – 1.03×', interpretation: 'Marginal advantage (within natural variance)' },
  { range: '1.04× – 1.07×', interpretation: 'Consistent overperformance' },
  { range: '1.08× – 1.15×', interpretation: 'Strong statistical advantage' },
  { range: '> 1.15×', interpretation: 'Exceptional, rare overperformance' },
];

// KPI Interpretation Table Component (Homepage only)
const KPIInterpretationTable = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-3">
      <CollapsibleTrigger className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-400 transition-colors mx-auto">
        <span className="underline underline-offset-2">How to read this</span>
        {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3">
        <div className="bg-gray-900/60 border border-gray-700/50 rounded-lg p-3 max-w-md mx-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-700/50">
                <th className="text-left text-gray-400 font-medium py-1.5 pr-3">KPI Value</th>
                <th className="text-left text-gray-400 font-medium py-1.5">Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {KPI_INTERPRETATION.map((row, idx) => (
                <tr key={idx} className="border-b border-gray-800/50 last:border-0">
                  <td className="text-amber-400/90 font-mono py-1.5 pr-3 whitespace-nowrap">{row.range}</td>
                  <td className="text-gray-300 py-1.5">{row.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-gray-500 mt-2 text-center">
            Small differences may require many draws to become statistically stable.
          </p>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Collapsible Message Component
const CollapsibleMessage = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useLanguage();
  
  return (
    <div className="text-center py-4 px-4">
      <p className="text-gray-400 text-sm">
        {t('home.collapsible.intro')}{' '}
        <br className="hidden sm:inline" />
        {t('home.collapsible.introCont')}{' '}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-500 hover:text-gray-300 inline-flex items-center gap-0.5 ml-1 transition-colors"
        >
          <span className="text-xs underline underline-offset-2">
            {isExpanded ? t('home.collapsible.readLess') : t('home.collapsible.readWhy')}
          </span>
          {isExpanded ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </button>
      </p>
      
      {isExpanded && (
        <div className="mt-4 max-w-2xl mx-auto text-gray-500 text-sm leading-relaxed space-y-3 text-left">
          <p>{t('home.collapsible.p1')}</p>
          <p>
            {t('home.collapsible.p2')}<br />
            {t('home.collapsible.p2cont')}
          </p>
          <p>{t('home.collapsible.p3')}</p>
          <p>
            {t('home.collapsible.p4')}<br />
            {t('home.collapsible.p4cont')}<br />
            {t('home.collapsible.p4end')}
          </p>
          <p>
            {t('home.collapsible.p5')}<br />
            {t('home.collapsible.p5cont')}
          </p>
          <p>{t('home.collapsible.p6')}</p>
          <p>
            {t('home.collapsible.p7')}<br />
            {t('home.collapsible.p7cont')}<br />
            {t('home.collapsible.p7end')}
          </p>
          <p>
            {t('home.collapsible.p8')}<br />
            {t('home.collapsible.p8cont')}<br />
            {t('home.collapsible.p8cont2')}<br />
            {t('home.collapsible.p8end')}
          </p>
        </div>
      )}
    </div>
  );
};

export const HomePage = () => {
  const { t, currentLang } = useLanguage();
  const [lotteries, setLotteries] = useState([]);
  const [latestDraws, setLatestDraws] = useState({});
  const [kpiHighlights, setKpiHighlights] = useState(null);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);
  const [dataVersion, setDataVersion] = useState(null);

  // STEP 1: Fetch lottery summary FIRST (render-first for cards)
  useEffect(() => {
    const fetchSummary = async (retryCount = 0) => {
      try {
        // PRIMARY: Try the new lightweight summary endpoint
        let summaryData = null;
        
        try {
          const summaryRes = await axios.get(`${API}/home/summary`, { 
            timeout: 8000,  // Increased timeout for mobile
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });
          summaryData = summaryRes.data;
          setDataVersion(summaryData.data_version);
          console.log('HomePage: Using /api/home/summary (single call)', summaryData.data_version);
        } catch (summaryErr) {
          console.warn('HomePage: /api/home/summary failed, falling back to /api/lotteries', summaryErr.message);
          setSummaryError(summaryErr.message);
        }
        
        // FALLBACK: Use old /api/lotteries endpoint if summary failed
        if (!summaryData || !summaryData.lotteries?.length) {
          const lotteriesRes = await axios.get(`${API}/lotteries`, { timeout: 8000 });
          const lotteryData = lotteriesRes.data || [];
          setLotteries(lotteryData);
          
          const drawsMap = {};
          lotteryData.forEach((lottery) => {
            if (lottery.latest_draw) {
              drawsMap[lottery.code] = lottery.latest_draw;
            }
          });
          setLatestDraws(drawsMap);
        } else {
          // Process summary data into the format components expect
          const processedLotteries = summaryData.lotteries.map(l => ({
            code: l.code,
            name: l.name,
            status: l.status,
            current_jackpot: l.next_jackpot_estimate || l.next_jackpot,  // Next draw estimate
            next_jackpot_estimate: l.next_jackpot_estimate || l.next_jackpot,  // Explicit field
            latest_draw_jackpot: l.latest_draw_jackpot,  // Prior draw jackpot
            jackpot_currency: l.currency,
            next_draw_date: l.next_draw_date,
            latest_draw: l.latest_draw
          }));
          setLotteries(processedLotteries);
          
          const drawsMap = {};
          summaryData.lotteries.forEach((l) => {
            if (l.latest_draw) {
              drawsMap[l.code] = l.latest_draw;
            }
          });
          setLatestDraws(drawsMap);
        }
      } catch (error) {
        console.error('Error fetching homepage summary:', error);
        
        // Retry once on mobile (network issues more common)
        if (retryCount < 1) {
          console.log('HomePage: Retrying fetch after error...');
          setTimeout(() => fetchSummary(retryCount + 1), 1000);
          return;
        }
      } finally {
        setCardsLoading(false);
      }
    };

    fetchSummary();
  }, []);

  // STEP 2: Fetch KPI LAST (deferred, after cards are visible)
  useEffect(() => {
    // Only fetch KPI after cards have loaded
    if (cardsLoading) return;
    
    // Defer KPI fetch to ensure cards render first
    const timeoutId = setTimeout(() => {
      const fetchKpi = async () => {
        try {
          console.log('HomePage: Fetching KPI (deferred)');
          const highlightsRes = await axios.get(`${API}/kpi-v2-highlights`, { timeout: 8000 });
          console.log('HomePage: KPI data received:', highlightsRes.data);
          setKpiHighlights(highlightsRes.data);
        } catch (kpiErr) {
          console.warn('HomePage: KPI highlights failed', kpiErr.message);
        } finally {
          setKpiLoading(false);
        }
      };
      fetchKpi();
    }, 150); // 150ms delay to ensure cards render first

    return () => clearTimeout(timeoutId);
  }, [cardsLoading]); // Only run when cardsLoading changes to false

  // Merge catalog item with real backend data (if present)
  // IMPORTANT: Use backend status as source of truth for pending state
  const getLotteryData = (catalogItem) => {
    // Find matching lottery from backend summary
    const backendData = lotteries.find((l) => l.code === catalogItem.code);
    
    if (backendData) {
      // Backend is source of truth for status
      const isPending = backendData.status === 'pending';
      return {
        ...backendData,
        name: catalogItem.name,
        country: catalogItem.country,
        isPending: isPending
      };
    }
    
    // Fallback: Use catalog's explicit pending flag if set, otherwise not pending
    // This prevents "Coming Soon" when API fails or data is loading
    return { 
      ...catalogItem, 
      isPending: catalogItem.pending === true  // Only pending if explicitly marked in catalog
    };
  };

  const pendingLotteries = Object.values(LOTTERY_CATALOG).flat().map(getLotteryData).filter(lottery => lottery.isPending);

  // Region names with i18n
  const regionNames = {
    EUROPE: t('home.regions.europe'),
    AMERICAS: t('home.regions.americas'),
    AUSTRALIA: t('home.regions.australia'),
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        description="Lotterics analyzes historical lottery data to study probability, randomness, and statistical model behavior. An educational platform focused on transparency, limitations, and measurable outcomes."
        path=""
      />

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* MAIN COLUMN */}
          <div className="lg:col-span-9">
            {/* HERO SECTION (CENTERED) */}
            <section className="text-center py-3">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-medium">
                  {t('home.hero.badge')}
                </span>
              </div>

              <h1
                className="text-3xl md:text-4xl font-bold text-white mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {t('home.hero.titleLine1')}
                <span className="block text-transparent bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 bg-clip-text">
                  {t('home.hero.titleLine2')}
                </span>
              </h1>

              <p className="text-gray-400 mb-4 text-sm max-w-2xl mx-auto">
                {t('home.hero.tagline')}
              </p>

              <div className="flex flex-wrap justify-center gap-2 mb-4">
                <Link
                  to={`/${currentLang}/predictions`}
                  className="px-5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-sm font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-amber-500/20 flex items-center gap-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  {t('home.cta.generate')}
                </Link>
                <Link
                  to={`/${currentLang}/statistics`}
                  className="px-5 py-2 bg-gray-800/50 hover:bg-gray-800 border border-gray-700/50 text-white text-sm font-semibold rounded-lg transition-all duration-300"
                >
                  {t('home.cta.statistics')}
                </Link>
              </div>
            </section>
            
            {/* COLLAPSIBLE MESSAGE - Discreet, under hero */}
            <CollapsibleMessage />

            {/* AGGREGATED KPI ROW - ABOVE TESTIMONIALS */}
            {kpiLoading ? (
              <section className="mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* KPI Skeleton placeholders */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-gray-700/50 rounded-lg animate-pulse" />
                        <div className="h-4 w-24 bg-gray-700/50 rounded animate-pulse" />
                      </div>
                      <div className="h-6 w-16 bg-gray-700/50 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              </section>
            ) : kpiHighlights && !kpiHighlights.error ? (
              <section className="mb-4" data-testid="homepage-kpi-section">
                {/* Show basic stats if predictions not ready */}
                {kpiHighlights.model_comparison?.basic_stats ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Total Lotteries */}
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-blue-400" />
                        </div>
                        <h4 className="text-white text-sm font-semibold">Active Lotteries</h4>
                      </div>
                      <div className="text-2xl font-bold text-blue-400">
                        {kpiHighlights.model_comparison.basic_stats.total_lotteries}
                        <span className="text-xs font-normal text-gray-400 ml-2">Worldwide</span>
                      </div>
                    </div>

                    {/* Total Draws */}
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                          <Sparkles className="w-4 h-4 text-amber-400" />
                        </div>
                        <h4 className="text-white text-sm font-semibold">Total Draws</h4>
                      </div>
                      <div className="text-2xl font-bold text-amber-400">
                        {kpiHighlights.model_comparison.basic_stats.total_draws}+
                        <span className="text-xs font-normal text-gray-400 ml-2">Analyzed</span>
                      </div>
                    </div>

                    {/* Latest Update */}
                    {kpiHighlights.model_comparison.basic_stats.latest_lottery && (
                      <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                            <Award className="w-4 h-4 text-green-400" />
                          </div>
                          <h4 className="text-white text-sm font-semibold">Latest Update</h4>
                        </div>
                        <div className="text-sm text-green-400 font-medium">
                          {kpiHighlights.model_comparison.basic_stats.latest_lottery}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (kpiHighlights.model_comparison?.statistical_avg_pi || 
                     kpiHighlights.model_comparison?.lotteryx_avg_pi || 
                     (kpiHighlights.highlights && kpiHighlights.highlights.length > 0)) ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Block A: Statistical Prediction Performance */}
                  {kpiHighlights.model_comparison?.statistical_avg_pi && (() => {
                    const kpi = kpiHighlights.model_comparison.statistical_avg_pi;
                    const kpiDisplay = kpi.toFixed(3);
                    const advantagePct = ((kpi - 1) * 100).toFixed(1);
                    const isPositive = kpi >= 1;
                    return (
                      <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-blue-400" />
                          </div>
                          <h4 className="text-white text-sm font-semibold">{t('home.kpi.statistical.title')}</h4>
                          <KPITooltip>
                            <button className="text-gray-500 hover:text-gray-400 transition-colors">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </KPITooltip>
                        </div>
                        <div className="text-xl font-bold text-blue-400" data-testid="statistical-kpi-value">
                          {kpiDisplay}×
                          <span className="text-xs font-normal text-gray-400 ml-1">Global Performance Index</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Block B: Lotterics Prediction Performance */}
                  {kpiHighlights.model_comparison?.lotteryx_avg_pi && (() => {
                    const kpi = kpiHighlights.model_comparison.lotteryx_avg_pi;
                    const kpiDisplay = kpi.toFixed(3);
                    const advantagePct = ((kpi - 1) * 100).toFixed(1);
                    const isPositive = kpi >= 1;
                    return (
                      <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-amber-500/10 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-4 h-4 text-amber-400" />
                          </div>
                          <h4 className="text-white text-sm font-semibold">{t('home.kpi.lotterics.title')}</h4>
                          <KPITooltip>
                            <button className="text-gray-500 hover:text-gray-400 transition-colors">
                              <Info className="w-3.5 h-3.5" />
                            </button>
                          </KPITooltip>
                        </div>
                        <div className="text-xl font-bold text-amber-400" data-testid="lotterics-kpi-value">
                          {kpiDisplay}×
                          <span className="text-xs font-normal text-gray-400 ml-1">Global Performance Index</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Block C: Notable Prediction (global) */}
                  {kpiHighlights.highlights && kpiHighlights.highlights.length > 0 && (
                    <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                          <Award className="w-4 h-4 text-green-400" />
                        </div>
                        <h4 className="text-white text-sm font-semibold">{t('home.kpi.notable.title')}</h4>
                      </div>
                      <div className="text-sm">
                        <span className="text-white font-medium">{kpiHighlights.highlights[0].lottery_name}</span>
                        <span className="text-gray-400 ml-2">
                          {kpiHighlights.highlights[0].main_hits}+{kpiHighlights.highlights[0].bonus_hits} {t('home.kpi.notable.match')}
                        </span>
                      </div>
                    </div>
                  )}
                  </div>
                ) : (
                  /* No KPI data yet */
                  <div className="text-center py-2">
                    <p className="text-gray-500 text-xs">
                      Performance metrics will be available soon.
                    </p>
                  </div>
                )}
              </section>
            ) : null}


            {/* LATEST INTELLIGENCE & ARTICLES FEED */}
    

            {/* LATEST RESULTS - 4 COLUMN LAYOUT */}
            <section className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg md:text-xl font-bold text-white">
                  {t('home.latestResults.title')}
                </h2>
              </div>

              {!cardsLoading && (
                <div className="space-y-5">
                  {['EUROPE', 'AMERICAS', 'AUSTRALIA'].filter(region => LOTTERY_CATALOG[region].some(item => !getLotteryData(item).isPending)).map((region) => (
                    <div key={region} className="space-y-2">
                      {/* Region Header */}
                      <div className="flex items-center justify-between">
                        <h3 className="text-amber-400 text-sm font-semibold">
                          {regionNames[region]}
                        </h3>
                        <Link
                          to={`/${currentLang}/lotteries`}
                          className="text-amber-500 hover:text-amber-400 text-xs font-medium"
                        >
                          <ChevronRight className="w-3 h-3" />
                        </Link>
                      </div>

                      {/* Lottery Cards - Stacked Vertically */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {LOTTERY_CATALOG[region].filter(item => !getLotteryData(item).isPending).map((item) => {
                          const data = getLotteryData(item);
                          const latest = latestDraws[item.code];

                          return (
                            <CompactLotteryCard
                              key={item.code}
                              lottery={data}
                              latestDraw={latest}
                              isPending={data.isPending}
                              className="hover:border-amber-500/40 hover:shadow-lg hover:shadow-amber-500/10 transition-all duration-200"
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {cardsLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-4 w-24 bg-gray-800 rounded animate-pulse" />
                      <div className="space-y-2">
                        {[1, 2, 3].map((j) => (
                          <div
                            key={j}
                            className="h-24 bg-gray-900/50 rounded-lg animate-pulse"
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {!cardsLoading && pendingLotteries.length > 0 && (
          <section className="mt-6 rounded-xl border border-gray-800 bg-gray-900/20 p-4" aria-labelledby="coming-soon-heading">
            <h2 id="coming-soon-heading" className="text-sm font-medium text-gray-400 mb-3">
              {t('lottery.comingSoon')}
            </h2>
            <ul className="flex flex-wrap gap-2">
              {pendingLotteries.map(lottery => (
                <li key={lottery.code} className="rounded-full border border-gray-800 px-3 py-1.5 text-xs text-gray-400">
                  {lottery.name}
                </li>
              ))}
            </ul>
          </section>
        )}

        <LatestIntelligenceFeed lang={currentLang} />

        {/* ALL LOTTERIES - COMPREHENSIVE SEO INTERNAL LINKS */}
            <section className="mt-8 mb-6">
              <AllLotteriesLinks title="All Lotteries" />
              
              {/* Additional subpage links for maximum crawl coverage */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { code: 'euromillions', name: 'EuroMillions' },
                  { code: 'powerball', name: 'Powerball' },
                  { code: 'megamillions', name: 'Mega Millions' },
                  { code: 'superenalotto', name: 'SuperEnalotto' },
                ].map((lottery) => (
                  <div key={lottery.code} className="bg-gray-900/20 border border-gray-800/30 rounded-lg p-2">
                    <h4 className="text-xs font-semibold text-gray-400 mb-1">{lottery.name}</h4>
                    <div className="flex flex-wrap gap-1">
                      <Link to={`/${currentLang}/lottery/${lottery.code}/results`} className="text-[10px] text-gray-500 hover:text-amber-400">Results</Link>
                      <span className="text-gray-700">•</span>
                      <Link to={`/${currentLang}/lottery/${lottery.code}/statistics`} className="text-[10px] text-gray-500 hover:text-amber-400">Stats</Link>
                      <span className="text-gray-700">•</span>
                      <Link to={`/${currentLang}/lottery/${lottery.code}/predictions`} className="text-[10px] text-gray-500 hover:text-amber-400">Predictions</Link>
                    </div>
                  </div>
                ))}
              </div>
            </section>

 {/* EDITORIAL CONTEXT - ADSENSE COMPLIANCE */}
<section className="mt-8 mb-6">
  <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-5">
    <h2 className="text-lg font-semibold text-white mb-3">{t('home.about.title')}</h2>
    
    <div className="space-y-3 text-sm text-gray-300 leading-relaxed">
      <p>
        <strong className="text-gray-200">{t('home.about.p1Strong')}</strong>{" "}
        {t('home.about.p1')}
      </p>
      
      <p>
        <strong className="text-gray-200">{t('home.about.p2Strong')}</strong>{" "}
        {t('home.about.p2')}
      </p>
      
      <p>
        <strong className="text-gray-200">{t('home.about.p3Strong')}</strong>{" "}
        {t('home.about.p3')}
      </p>
    </div>
  </div>
</section>

            {/* LEGAL DISCLAIMER */}
            <section className="mt-6">
              <div className="bg-gray-900/30 border border-gray-700/30 rounded-lg p-2">
                <p className="text-gray-500 text-[10px] text-center">
                  <strong className="text-gray-400">{t('home.disclaimer.label')}</strong> {t('home.disclaimer.text')}
                </p>
              </div>
            </section>

            {/* DATA VERSION STAMP - Visible proof of data freshness */}
            {dataVersion && (
              <div className="mt-4 flex justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-900/60 border border-gray-700/50 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-[10px] font-mono text-gray-400">
                    data_version: <span className="text-green-400 font-semibold">v{dataVersion}</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* SIDEBAR (RIGHT, STICKY AD SPACE) */}
          <div className="lg:col-span-3">
            <div className="sticky top-4 space-y-4">
              <div className="bg-gray-900/40 border border-gray-700/50 rounded-lg p-3">
                {/* You can later replace this with a real <AdBanner /> */}
                <div className="w-full h-[600px] bg-gradient-to-b from-amber-500/10 to-amber-600/5 rounded-lg flex items-center justify-center border border-amber-500/20">
                  <div className="text-center">
                    <div className="text-amber-500/60 text-sm font-medium mb-1">
                      {t('home.sidebar.ad.title')}
                    </div>
                    <div className="text-amber-500/40 text-xs">300 × 600</div>
                    <div className="text-amber-500/30 text-[10px] italic mt-1">
                      {t('home.sidebar.ad.subtitle')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Optional extra ad or donate promo later */}
              {/* <AdBanner placement="homepage-sidebar-bottom" /> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
